import { Component, type ErrorInfo, type ReactNode } from 'react';
import { handleError } from '@/utils/errorHandler';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  showDetails: boolean;
}

const IS_PROD = import.meta.env.PROD;

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
    showDetails: false,
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    handleError(error, { level: 'critical', message: 'Errore non gestito nell\'applicazione', operation: 'ErrorBoundary.componentDidCatch' });
    this.setState({ errorInfo });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null, showDetails: false });
  };

  private toggleDetails = () => {
    this.setState((prev) => ({ showDetails: !prev.showDetails }));
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          padding: '40px',
          backgroundColor: '#1a1a2e',
          color: '#fff',
          minHeight: '100vh',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{ maxWidth: '480px', width: '100%', textAlign: 'center' }}>
            {/* Icon */}
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '16px',
              background: 'linear-gradient(135deg, rgba(255,107,107,0.2), rgba(255,107,107,0.1))',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px',
            }}>
              <span style={{ fontSize: '28px' }}>!</span>
            </div>

            <h1 style={{ color: '#fff', marginBottom: '8px', fontSize: '20px', fontWeight: 600 }}>
              Qualcosa è andato storto
            </h1>
            <p style={{ color: '#8b8fa3', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
              Si è verificato un errore imprevisto. Puoi provare a ricaricare la pagina o tornare alla schermata principale.
            </p>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '24px' }}>
              <button
                onClick={this.handleReload}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#6c5ce7',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Ricarica pagina
              </button>
              <button
                onClick={this.handleReset}
                style={{
                  padding: '10px 20px',
                  backgroundColor: 'transparent',
                  color: '#8b8fa3',
                  border: '1px solid #2d2d44',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Riprova
              </button>
            </div>

            {/* Toggle details */}
            {!IS_PROD || this.state.showDetails ? (
              <>
                <button
                  onClick={this.toggleDetails}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#6c5ce7',
                    cursor: 'pointer',
                    fontSize: '12px',
                    marginBottom: '12px',
                  }}
                >
                  {this.state.showDetails ? 'Nascondi dettagli tecnici' : 'Mostra dettagli tecnici'}
                </button>

                {this.state.showDetails && (
                  <div style={{
                    backgroundColor: '#16213e',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'left',
                  }}>
                    <pre style={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: '11px',
                      color: '#feca57',
                      margin: 0,
                    }}>
                      {this.state.error?.toString()}
                    </pre>
                    {this.state.errorInfo && (
                      <pre style={{
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        fontSize: '10px',
                        color: '#8b8fa3',
                        marginTop: '8px',
                        maxHeight: '200px',
                        overflow: 'auto',
                      }}>
                        {this.state.errorInfo.componentStack}
                      </pre>
                    )}
                  </div>
                )}
              </>
            ) : (
              <button
                onClick={this.toggleDetails}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#6c5ce7',
                  cursor: 'pointer',
                  fontSize: '12px',
                }}
              >
                Mostra dettagli tecnici
              </button>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
